import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ShiftContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ShiftProvider({ children }) {
  const { user } = useAuth();
  const [asistencia, setAsistencia] = useState(undefined); // undefined = loading
  const [corte, setCorte] = useState(null);
  const [puntoNombre, setPuntoNombre] = useState('');

  const isSupervisor = user && (user.role === 'admin' || user.role === 'propietario');

  const loadShift = useCallback(async () => {
    if (!user || user === false) {
      setAsistencia(null);
      return;
    }
    // admin/propietario no abren su propio turno: se les muestra el turno activo
    // (folio, fecha) de la sucursal ligada a su cuenta, lo haya abierto quien lo haya abierto.
    if (isSupervisor && !user.punto_venta_id) {
      setAsistencia(null);
      return;
    }
    try {
      const { data: asist } = isSupervisor
        ? await axios.get(`${API}/asistencia/activa/${user.punto_venta_id}`)
        : await axios.get(`${API}/asistencia/hoy`);
      setAsistencia(asist || null);
      if (asist?.punto_venta_id) {
        const [corteRes, puntosRes] = await Promise.allSettled([
          axios.get(`${API}/corte/hoy/${asist.punto_venta_id}`),
          axios.get(`${API}/puntos-venta`),
        ]);
        setCorte(corteRes.status === 'fulfilled' ? corteRes.value.data : null);
        if (puntosRes.status === 'fulfilled') {
          const punto = puntosRes.value.data.find(p => p.id === asist.punto_venta_id);
          setPuntoNombre(punto?.name || '');
        }
      } else {
        setCorte(null);
        setPuntoNombre('');
      }
    } catch {
      setAsistencia(null);
    }
  }, [user, isSupervisor]);

  useEffect(() => {
    loadShift();
    // El supervisor no abre/cierra su propio turno (eso lo hace el vendedor en otro
    // dispositivo), así que se refresca periódicamente para reflejar esos cambios.
    if (isSupervisor && user?.punto_venta_id) {
      const interval = setInterval(loadShift, 45000);
      return () => clearInterval(interval);
    }
  }, [loadShift, isSupervisor, user]);

  const isOpen = !!asistencia && !corte;
  const isClosed = !!asistencia && !!corte;

  return (
    <ShiftContext.Provider value={{ asistencia, corte, puntoNombre, isOpen, isClosed, loading: asistencia === undefined, refresh: loadShift }}>
      {children}
    </ShiftContext.Provider>
  );
}

export function useShift() {
  const ctx = useContext(ShiftContext);
  if (!ctx) throw new Error('useShift must be used within ShiftProvider');
  return ctx;
}
